import { InputGroup, Text } from "@smartsuite/react-ui/lib";
import cx from "classnames";
import React, { useMemo } from "react";
import { NumericFormat } from "react-number-format";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import { FormFieldProps } from "../../../types";
import { TextFieldValue } from "../../text/text.config";
import { validateByType } from "../../text/validation";
import { PlainTextFieldProps } from "@smartsuite/react-ui/lib/components/forms/PlainTextField/PlainTextField";
import { MAX_PRECISION } from "@smartsuite/types";
import { currencies } from "./utils";
import { getSeparators, isAllowedValue } from "../../number/NumberFieldControl/utils";
import "./CurrencyFieldControl.sass";

export type CurrencyFieldControlProps = FormFieldProps &
  Pick<PlainTextFieldProps, "placeholder" | "type">;

export const CurrencyFieldControl: React.FunctionComponent<CurrencyFieldControlProps> = ({
  caption,
  className,
  field,
  formItem,
  label,
  name,
  placeholder,
  required,
  type,
  readOnly,
}) => {
  const formField = useFormField<TextFieldValue>({
    name,
    validate: validateByType(type, field, formItem),
  });

  const currencyCode = field.params.currency;
  const currency = useMemo(
    () => currencies.find(({ code }) => code === currencyCode),
    [currencyCode]
  );

  const inputClassName = cx("form-currency-field", className, {
    "has-value": !!formField.value,
  });

  const separators = getSeparators(field.params);

  const fieldParameters = useMemo(
    () => ({
      max_length: field.params.max_length,
      allow_negative: field.params.allow_negative,
      decimal_separator: separators.decimal_separator,
      thousand_separator: separators.thousand_separator,
    }),
    [
      field.params.allow_negative,
      field.params.max_length,
      separators.decimal_separator,
      separators.thousand_separator,
    ]
  );

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      errorMessage={formField.errorMessage}
      label={label}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      <InputGroup className="form-currency-field-group" state={formField.state}>
        <NumericFormat
          className={inputClassName}
          value={formField.value}
          onBlur={formField.onBlur}
          onValueChange={(values) => {
            formField.onChange(values.floatValue ?? "");
          }}
          onFocus={formField.onFocus}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          decimalScale={MAX_PRECISION}
          decimalSeparator={fieldParameters.decimal_separator}
          thousandSeparator={fieldParameters.thousand_separator}
          isAllowed={({ formattedValue }) => isAllowedValue(formattedValue, fieldParameters)}
          name={formField.name}
          allowNegative={fieldParameters.allow_negative}
        />

        {currency && (
          <Text size="l" color="secondary" className="form-currency-field__legend">
            {currency.code} - {currency.sign}
          </Text>
        )}
      </InputGroup>
    </FormFieldControl>
  );
};
